import { UncontrolledTooltip } from 'reactstrap';
import styled from 'styled-components';
import { asRelativeTime } from './formatters';
import SectionsIcon from '../components/icons/SectionsIcon';
import { useUser } from './hooks';
import { useIsMobile } from './responsive';
import FarmActionsDropdown from '../components/farm/FarmActionsDropdown';
import FarmTagsCell from '../components/FarmTagsCell';
import LockIcon from '../components/icons/LockIcon';
import useMutateWithState from '../hooks/useMutateWithState';

export const timeColumns = [
  {
    accessor: 'createdAt',
    displayString: 'Created',
    format: asRelativeTime,
  },
  {
    accessor: 'updatedAt',
    displayString: 'Updated',
    format: asRelativeTime,
  },
];

const FarmGridIcon = styled(SectionsIcon)`
  color: var(--overyield-orange);
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

const FarmLockIcon = styled(LockIcon)`
  color: #688460; // Dark green from the overyield logo
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

const FarmNameCell = styled(({ name, className, farm }) => (
  <div className={`font-weight-bold d-flex align-items-center ${className}`}>
    {farm.isLocked ? (
      <FarmLockIcon id={`farm-grid-icon-${farm.id}`} />
    ) : (
      <FarmGridIcon id={`farm-grid-icon-${farm.id}`} />
    )}
    <UncontrolledTooltip
      placement="top"
      target={`farm-grid-icon-${farm.id}`}
      innerClassName="font-tiny"
      key={`icon-tooltip-${farm.id}-${farm.isLocked}`} // prevents issue where tooltip doesn't update
    >
      {farm.isLocked ? 'Locked' : 'Not locked'}
    </UncontrolledTooltip>
    <span title={name} className="ml-2 text-truncate">
      {name}
    </span>
  </div>
))`
  color: var(--text-color);
  min-width: var(--table-column-width);
`;

export const farmOwnerText = (owner, user) => {
  const isOwner = user && user.id === owner.id;
  return isOwner ? 'Myself' : `${owner.firstName} ${owner.lastName}`;
};

const FarmOwnerCell = ({ owner }) => {
  const user = useUser();
  return <span title={owner.email}>{farmOwnerText(owner, user)}</span>;
};

export const farmNameColumn = {
  accessor: 'name',
  displayString: 'Name',
  // eslint-disable-next-line react/display-name
  format: (name, farm) => <FarmNameCell name={name} farm={farm} />,
};

export const farmUpdatedAtColumn = {
  accessor: 'objectUpdatedAt',
  displayString: 'Last Modified',
  format: asRelativeTime,
};

export const farmTeamColumn = {
  accessor: (farm) => farm.team?.name,
  displayString: 'Team',
  format: (team) => team || 'None',
};

export const farmOwnerColumn = {
  accessor: (farm) => farm.owner,
  displayString: 'Owner',
  // eslint-disable-next-line react/display-name
  format: (owner) => <FarmOwnerCell owner={owner} />,
  thStyle: { minWidth: 'revert' },
};

const mobileFarmColumns = [farmNameColumn, farmUpdatedAtColumn];

const getAllFarmColumns = (mutateAndSetState, refetchQuery) => [
  {
    accessor: (farm) => farm,
    // eslint-disable-next-line react/display-name
    format: (farm) => (
      <FarmActionsDropdown
        farm={farm}
        refetchQuery={refetchQuery}
        mutateAndSetState={mutateAndSetState}
      />
    ),
    thStyle: { minWidth: 'revert', width: 'max-content' },
  },
  farmNameColumn,
  {
    accessor: 'tags',
    displayString: 'Tags',
    // eslint-disable-next-line react/display-name
    format: (tags, item) => (
      <FarmTagsCell
        tags={tags}
        item={item}
        mutateAndSetState={mutateAndSetState}
      />
    ),
  },
  {
    ...farmUpdatedAtColumn,
    // the size of the longest time string ("a few seconds ago")
    // to avoid the column jumping around when the time changes
    thStyle: { minWidth: '130px' },
  },
  farmTeamColumn,
  farmOwnerColumn,
];

export const useFarmColumns = (refetchQuery) => {
  const isMobile = useIsMobile();
  const [, mutateAndSetState] = useMutateWithState();
  const farmColumns = isMobile
    ? mobileFarmColumns
    : getAllFarmColumns(mutateAndSetState, refetchQuery);
  return { farmColumns };
};

export const YEARLY_METRIC_SUFFIX = 'ByYear';

export const getMetricsForYear = (cols) =>
  cols.map(({ accessor, ...metric }) => ({
    ...metric,
    // Note that for this to work as expected, yearly accessor names must be a string
    accessor:
      typeof accessor === 'string'
        ? accessor.replace(YEARLY_METRIC_SUFFIX, '')
        : accessor,
  }));

export const getYearlyMetrics = (cols) =>
  cols.filter(({ accessor }) =>
    typeof accessor === 'string'
      ? accessor.endsWith(YEARLY_METRIC_SUFFIX)
      : false,
  );
